import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { MicroAppSummary } from "@/api";
import { listMicroAppsOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { EntityListPage } from "@/components/build/EntityListPage";
import { EntityRow } from "@/components/build/EntityRow";
import { byName, byNewest, useListControls } from "@/components/build/useListControls";
import { BuildWithAiButton } from "@/features/build/withAi/BuildWithAiButton";
import { MicroAppIcon } from "./MicroAppIcon";
import { useDeleteMicroApp } from "./useDeleteMicroApp";

/** "Mine" = apps I published (not shared with me). */
const isMine = (a: MicroAppSummary) => !a.isShared;

/** Build > Micro-apps list. Creation is chat-based, so New opens an explainer. */
export function MicroAppsListPage() {
  const { t } = useTranslation(["build", "microApps"]);
  const navigate = useNavigate();
  const { onDelete, dialog } = useDeleteMicroApp();
  const { search, apply, listProps } = useListControls<MicroAppSummary>({
    isMine,
    sorts: [
      { value: "name", label: t("build:list.sort.name"), compare: byName },
      { value: "newest", label: t("build:list.sort.newest"), compare: byNewest },
    ],
  });

  const query = useQuery(listMicroAppsOptions({ query: { page_size: 50 } }));
  const apps = apply(query.data?.data);

  return (
    <EntityListPage
      title={t("microApps:title")}
      subtitle={t("microApps:subtitle")}
      newLabel={t("microApps:new")}
      onNew={() => navigate("/build/apps/new")}
      headerActions={<BuildWithAiButton entity="micro_app" />}
      {...listProps}
    >
      {apps.map((app) => (
        <EntityRow
          key={app.uuid}
          icon={<MicroAppIcon />}
          name={app.name}
          description={app.description}
          onOpen={() => navigate(`/build/apps/${app.uuid}`)}
          menuItems={[
            { label: t("microApps:detail.open"), onSelect: () => navigate(`/build/apps/${app.uuid}/view`) },
            { label: t("build:menu.edit"), onSelect: () => navigate(`/build/apps/${app.uuid}`) },
            { label: t("build:menu.delete"), danger: true, onSelect: () => onDelete(app) },
          ]}
        />
      ))}
      {apps.length === 0 &&
        (search ? (
          <EmptyState title={t("build:list.noResultsTitle")} body={t("build:list.noResultsBody", { query: search })} />
        ) : (
          <EmptyState
            icon={<LayoutDashboard size={32} />}
            title={t("microApps:empty.title")}
            body={t("microApps:empty.body")}
          />
        ))}
      {dialog}
    </EntityListPage>
  );
}
